import React, { Fragment, useEffect, useState } from 'react';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { pathOr } from 'ramda';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  txIsEnquired,
} from '../../util/transactions';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, ensureTransaction } from '../../util/data';
import { withViewport } from '../../util/contextHelpers';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import {
  fetchMessages,
  fetchMoreMessages,
  fetchTransaction,
  openedEnquiry,
  sendMessage,
  sendReview,
} from '../TransactionPage/TransactionPage.duck';
import {
  Page,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperSideNav,
  LayoutWrapperTopbar,
  NamedLink,
} from '../../components';
import { TopbarContainer } from '../../containers';
import config from '../../config';
import { loadData, updateMessageBookingEvent } from './InboxPage.duck';
import DetailsSection from './DetailsSection';
import FeedSection from './FeedSection';
import css from './InboxPage.module.css';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { handleMessageEvent, txState } from './InboxPage';

const { UUID } = sdkTypes;

export const MobileChatPageComponent = props => {
  const {
    currentUser,
    scrollingDisabled,
    fetchInProgress,
    fetchMessagesInProgress,
    fetchMessagesError,
    fetchOrdersOrSalesError,
    fetchTransactionError,
    messages,
    messagesNotificationCount,
    oldestMessagePageFetched,
    onFetchMessages,
    onFetchTransaction,
    onManageDisableScrolling,
    onOpenEnquiry,
    onSendMessage,
    onSendReview,
    onUpdateProfile,
    onShowMoreMessages,
    params,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    totalMessagePages,
    transaction,
    inboxTransactions,
    unitType,
    intl,
    onUpdateMessageBookingEvent,
  } = props;
  const { id } = params;
  const isMessagesTab = true;
  const [currentTransaction, setCurrentTransaction] = useState(transaction);
  const [canHandleMessageEvent, setCanHandleMessageEvent] = useState(true);
  useEffect(() => {
    if (!isMessagesTab || messagesNotificationCount < 0) return;

    onUpdateProfile({ privateData: { messagesBadge: 0 } });
  }, [isMessagesTab]);
  useEffect(() => {
    if (!transaction || !canHandleMessageEvent) return;

    setCurrentTransaction(transaction);
    setCanHandleMessageEvent(false);
    handleMessageEvent(transaction, currentUser, onUpdateMessageBookingEvent);
  }, [transaction]);
  useEffect(() => {
    if (!id) return;

    onFetchMessages(id, 1);
    onFetchTransaction(new UUID(id));
  }, [id]);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserId = pathOr(null, ['id', 'uuid'], ensuredCurrentUser);
  const txProviderId = pathOr(null, ['provider', 'id', 'uuid'], currentTransaction);
  const type = currentTransaction && currentUserId && txProviderId && currentUserId === txProviderId ? 'sale' : 'order';
  const stateData = currentTransaction && txState(intl, currentTransaction, type);
  const title = intl.formatMessage({ id: 'InboxPage.title' });
  const fetchCurrentTxError = fetchTransactionError ? (
    <p className={css.error}>
      <FormattedMessage id='InboxPage.fetchTransactionFailed' />
    </p>
  ) : null;
  const isOrder = type === 'order';
  const isEnquiry = txIsEnquired(currentTransaction);
  const moreDetailsMessage = intl.formatMessage({ id: 'InboxPage.moreDetails'});
  const protectedData = pathOr({}, ['attributes', 'protectedData'], currentTransaction);
  const canShowSearch = stateData && stateData.showSearch && protectedData.fromSearch;
  const searchMoreMessage = intl.formatMessage({ id: 'InboxPage.searchMore' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer className={css.topbar} currentPage='InboxPage' />
        </LayoutWrapperTopbar>
        <LayoutWrapperSideNav />
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          {currentTransaction && !fetchTransactionError ? (
            <Fragment>
              {!isEnquiry ? (
                <div className={css.mobileLinkContainer}>
                  <NamedLink
                    className={css.moreDetailsLink}
                    name={isOrder ? 'OrderDetailsPage' : 'SaleDetailsPage'}
                    params={{ id: currentTransaction.id.uuid }}
                  >
                    {moreDetailsMessage}
                  </NamedLink>
                </div>
              ) : null}
              {canShowSearch ? (
                <NamedLink
                  className={css.searchMoreLink}
                  name='SearchPage'
                  to={{ search: protectedData.fromSearch }}
                >
                  <span>{searchMoreMessage}</span>
                </NamedLink>
              ) : null}
              <FeedSection
                className={css.activityFeedContainerMobileChat}
                currentTransaction={currentTransaction}
                currentUser={currentUser}
                fetchMessagesError={fetchMessagesError}
                fetchMessagesInProgress={fetchMessagesInProgress}
                messages={messages}
                oldestMessagePageFetched={oldestMessagePageFetched}
                onManageDisableScrolling={onManageDisableScrolling}
                onSendMessage={onSendMessage}
                onSendReview={onSendReview}
                onShowMoreMessages={() => onShowMoreMessages(currentTransaction.id)}
                sendMessageError={sendMessageError}
                sendMessageInProgress={sendMessageInProgress}
                sendReviewInProgress={sendReviewInProgress}
                sendReviewError={sendReviewError}
                totalMessagePages={totalMessagePages}
                type={type}
                intl={intl}
              />
              <DetailsSection
                className={css.transactionDetailsContainer}
                currentTransaction={currentTransaction}
                currentUser={currentUser}
                stateData={stateData}
                type={type}
                unitType={unitType}
                intl={intl}
              />
            </Fragment>
          ) : (
            <Fragment>
              <div className={css.activityFeedContainer}>
                <div className={css.topFeedContainer} />
              </div>
              <div className={css.transactionDetailsContainer}>
                <div className={css.topDetailsContainer}>
                  <FormattedMessage id='InboxPage.details' />
                </div>
                {fetchCurrentTxError}
              </div>
            </Fragment>
          )}
        </LayoutWrapperMain>
      </LayoutSideNavigation>
    </Page>
  );
};

MobileChatPageComponent.defaultProps = {
  currentUser: null,
  currentUserHasOrders: null,
  fetchMessagesError: null,
  fetchOrdersOrSalesError: null,
  fetchTransactionError: null,
  messagesNotificationCount: 0,
  pagination: null,
  sendMessageError: null,
  sendVerificationEmailError: null,
  transaction: null,
  unitType: config.bookingUnitType,
};

MobileChatPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  fetchMessagesError: propTypes.error,
  fetchOrdersOrSalesError: propTypes.error,
  fetchTransactionError: propTypes.error,
  inboxTransactions: arrayOf(propTypes.transaction).isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  messagesNotificationCount: number,
  onSendMessage: func.isRequired,
  onUpdateProfile: func.isRequired,
  onShowMoreMessages: func.isRequired,
  onUpdateMessageBookingEvent: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  unitType: propTypes.bookingUnitType,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { fetchInProgress, fetchOrdersOrSalesError, pagination, transactionRefs } = state.InboxPage;
  const {
    fetchMessagesInProgress,
    fetchMessagesError,
    fetchTransactionError,
    messages,
    oldestMessagePageFetched,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    totalMessagePages,
    transactionRef,
  } = state.TransactionPage;
  const {
    currentUser,
  } = state.user;
  const messagesNotificationCount = pathOr(0, ['attributes', 'profile', 'privateData', 'messagesBadge'], currentUser);
  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  const transaction = transactions.length > 0 ? transactions[0] : null;

  return {
    currentUser,
    fetchInProgress,
    fetchMessagesError,
    fetchMessagesInProgress,
    fetchOrdersOrSalesError,
    fetchTransactionError,
    inboxTransactions: getMarketplaceEntities(state, transactionRefs),
    messages,
    messagesNotificationCount,
    oldestMessagePageFetched,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    totalMessagePages,
    transaction,
    transactionRef,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onShowMoreMessages: txId => dispatch(fetchMoreMessages(txId)),
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    onFetchMessages: (txId, page) => dispatch(fetchMessages(txId, page)),
    onFetchTransaction: (txId, txRole) => dispatch(fetchTransaction(txId, txRole)),
    onOpenEnquiry: (txId) => dispatch(openedEnquiry(txId)),
    onUpdateProfile: (...data) => dispatch(updateProfile(...data)),
    onUpdateMessageBookingEvent: (...args) => dispatch(updateMessageBookingEvent(...args)),
  };
};

const MobileChatPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withViewport,
  injectIntl,
)(MobileChatPageComponent);

MobileChatPage.loadData = loadData;

export default MobileChatPage;



// WEBPACK FOOTER //
// ./src/containers/InboxPage/MobileChatPage.js